import React, { useState } from 'react';
import { GameState, SeasonSummary } from '../../domain/entities';
import { AdPlaceholder } from '../shared/AdPlaceholder';
import { HistoryHelpModal } from '../shared/HistoryHelpModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHistory } from '@fortawesome/free-solid-svg-icons';

interface HistoryScreenProps {
    gameState: GameState;
}

export const HistoryScreen: React.FC<HistoryScreenProps> = ({ gameState }) => {
    const [isHelpOpen, setIsHelpOpen] = useState(false);
    const seasonSummaries = gameState.seasonHistory || [];

    return (
        <>
            <AdPlaceholder />
            <div className="main-content" style={{ padding: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h1><FontAwesomeIcon icon={faHistory} style={{ marginRight: '8px', color: 'var(--positive-color)' }} />Season History</h1>
                    <button
                        className="button"
                        onClick={() => setIsHelpOpen(true)}
                        style={{ padding: '8px 12px', fontSize: '0.9em' }}
                        title="Help"
                    >
                        Help
                    </button>
                </div>
                <p style={{ marginBottom: '24px', opacity: 0.9 }}>
                    Review past seasons and match results. Analyze your progress and track your managerial journey.
                </p>

                {seasonSummaries.length === 0 ? (
                    <div className="panel" style={{ textAlign: 'center', padding: '32px 16px' }}>
                        <p style={{ color: 'var(--text-secondary)', fontSize: '1.1em' }}>
                            No season history yet. Complete your first season to see end-of-season summaries here.
                        </p>
                    </div>
                ) : (
                    <>
                {seasonSummaries.map((summary) => (
                    <div key={summary.season} style={{ marginBottom: '32px' }}>
                        <h2 style={{ color: 'var(--accent-color)', marginBottom: '16px' }}>
                            Season {summary.season}
                        </h2>

                        {/* League Summaries */}
                        <div style={{ marginBottom: '24px' }}>
                            <h3 style={{ marginBottom: '12px' }}>Leagues</h3>
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                                gap: '16px',
                                marginBottom: '16px'
                            }}>
                                {Object.entries(summary.leagueSummaries).map(([leagueName, stats]) => (
                                    <div
                                        key={leagueName}
                                        className="panel"
                                        style={{
                                            borderLeftColor: 'var(--accent-color)',
                                        }}
                                    >
                                        <div className="panel-header">{leagueName}</div>
                                        <div className="panel-content">
                                            <div style={{ marginBottom: '12px' }}>
                                                <strong>Champion:</strong> {stats.champion}
                                            </div>

                                            {stats.promoted.length > 0 && (
                                                <div style={{ marginBottom: '12px' }}>
                                                    <strong style={{ color: 'var(--positive-color)' }}>
                                                        Promoted ({stats.promoted.length}):
                                                    </strong>
                                                    <div style={{ marginLeft: '12px', marginTop: '4px' }}>
                                                        {stats.promoted.map((team) => (
                                                            <div key={team}>{team}</div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {stats.relegated.length > 0 && (
                                                <div style={{ marginBottom: '12px' }}>
                                                    <strong style={{ color: 'var(--negative-color)' }}>
                                                        Relegated ({stats.relegated.length}):
                                                    </strong>
                                                    <div style={{ marginLeft: '12px', marginTop: '4px' }}>
                                                        {stats.relegated.map((team) => (
                                                            <div key={team}>{team}</div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {stats.topScorer && (
                                                <div style={{ marginBottom: '12px' }}>
                                                    <strong style={{ color: 'var(--gold-color)' }}>
                                                        Top Scorer:
                                                    </strong>
                                                    <div style={{ marginLeft: '12px', marginTop: '4px' }}>
                                                        {stats.topScorer.name} ({stats.topScorer.goals} goals)
                                                    </div>
                                                </div>
                                            )}

                                            {stats.topRated && (
                                                <div>
                                                    <strong style={{ color: 'var(--info-color)' }}>
                                                        Highest Rated:
                                                    </strong>
                                                    <div style={{ marginLeft: '12px', marginTop: '4px' }}>
                                                        {stats.topRated.name} (Skill: {stats.topRated.rating})
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Cup Winners */}
                        {Object.keys(summary.cupWinners).length > 0 && (
                            <div>
                                <h3 style={{ marginBottom: '12px' }}>Cup Winners</h3>
                                <div className="panel">
                                    <div className="panel-content">
                                        {Object.entries(summary.cupWinners).map(([cupName, winner]) => (
                                            <div key={cupName} style={{ marginBottom: '8px' }}>
                                                <strong>{cupName}:</strong> {winner || 'In Progress'}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                ))}
                    </>
                )}

                <HistoryHelpModal isOpen={isHelpOpen} onClose={() => setIsHelpOpen(false)} />
            </div>
        </>
    );
};
